import { Marketplace } from '../marketplaces/marketplaces.types';
import { Device } from '../devices/devices.types';
import { TablePaginationOptions } from '../tables/tables.types';
import { User } from '../users/users.types';

export interface Buyback {
  id: number;
  price: number;
  market_country: string;
  mp_buyback_id: string;
  buyback_date: string;
  bm_status:
    | 'TO_SEND'
    | 'SENT'
    | 'RECEIVED'
    | 'OFFER'
    | 'COUNTER_PROPOSAL'
    | 'VALIDATED'
    | 'MONEY_TRANSFERED'
    | 'PAID'
    | 'SUSPENDED'
    | 'CANCELED';
  description: string;
  sku_marketplace: string;
  sku_okamac: string;
  shipping_info: CustomerAdrress;
  marketplace_grade: string;
  currency: string;
  marketplace: Marketplace;
  tracking_number: string;
  is_received: boolean;
  is_unpacked: boolean;
  sn_sent: boolean;
  box: boolean;
  charger: boolean;
  other_item: number;
  other_item_description: string | null;
  price_counter_offer: number | null;
  comment_counter_offer: string | null;
  bm_reasons_counter_offer: string[] | null;
  device_id: number | null;
  device?: Device;
  rma_file: string;
  cancellation_file?: string | File;
  receival_date: string | null;
  max_processing_date: string | null;
  suspended_step: SuspendedStep | null;
  reasons_suspended: string[] | null;
  cancel_step?: 'to_ship' | 'to_send' | 'shipped' | 'returned' | 'to_refund' | null;
  return_tracking_number: string | null;
  return_label_url: string | null;
  manual_tracking_number: string | null;
  computer_received_at: string | null;
  handed_to_shipping_at: string | null;
  handed_to_shipping_by: User | null;
  return_supplier_at: string | null;
  refunded_at: string | null;
  ship: boolean;
  acquirable: boolean;
  acquired_at: string | null;
  urgency: BuybackUrgency | null;
  reservation: boolean;
  reservation_photos: ReservationPhoto[];
  suspension_photos: SuspensionPhoto[];
  // Commission rate (in percent) frozen on this buyback at creation, exposed under both keys
  commission: number | null;
  commission_rate?: number | null;
  // Commissioned price computed server-side from the frozen rate, never recomputed client-side
  commission_price?: number | null;
  rma_to_do: boolean;
}

export interface BuybackCommissionRate {
  commission_rate: number;
}

export interface ReservationPhoto {
  id: number;
  url: string;
}

export interface SuspensionPhoto {
  id: number;
  url: string;
}

export type SuspendedStep = 'contact_supplier' | 'supplier_contacted' | 'pending_verification';

export interface CustomerAdrress {
  city: string;
  phone: string;
  country: string;
  address1: string;
  address2: string;
  zip_code: string;
  last_name: string;
  first_name: string;
}

export interface BuybackListOptions {
  filters?: Partial<Record<keyof BuybackListOptions, string[]>> & { fulltext: string; bm_status: string };
  pagination?: TablePaginationOptions;
}

export interface BuybackCheckMessage {
  message: string;
  status: string;
}

export interface CounterOfferReason {
  id: string;
  label: string;
}

export interface CounterOfferReasonGroup {
  id: string;
  label: string;
  reasons: CounterOfferReason[];
}

export interface CounterOfferPayload {
  price: number;
  reasons: string[];
  comment?: string;
}

export interface SuspendReason {
  message: string;
  identifier: string;
  category: string;
}

export type UrgencyPole = 'reception' | 'deballage' | 'controle' | 'achat';
export type UrgencyLevel = 'green' | 'yellow' | 'red';

export interface BuybackUrgency {
  pole: UrgencyPole;
  deadline: string;
  start_time: string;
  remaining_seconds: number;
  percentage_remaining: number;
  level: UrgencyLevel;
}

export interface UrgentCount {
  reception: number;
  deballage: number;
  controle: number;
  achat: number;
  canceled_controle: number;
  canceled_expe: number;
}

export interface TabCount {
  validated_to_reflow: number;
  to_acquire: number;
  offer_nc_validated: number;
  suspended_to_suspend: number;
  suspended_supplier_contacted: number;
  suspended_pending_verification: number;
  suspended_contact_supplier: number;
  canceled_to_ship: number;
  canceled_to_send: number;
  canceled_to_refund: number;
  return_supplier: number;
}

// Return label can either be a Shyppi ZPL label or a PDF hosted at pdf_url
export interface BuybackReturnLabel {
  zpl?: string;
  pdf_url?: string;
}

export interface BuybackMoveStep {
  value: string;
  label: string;
  group?: string;
  requiresDevice?: boolean;
}

// Only destination steps accepted by POST /buybacks/:id/move ; any other value returns a 422
// Sub-tabs (offer_nc_*, validated_to_reflow) are resolved by the back and must not be proposed
export const BUYBACK_MOVE_STEPS: BuybackMoveStep[] = [
  { value: 'to_send', label: "En attente d'envoi", group: 'Envoyé' },
  { value: 'sent', label: 'Envoyé', group: 'Envoyé' },
  { value: 'received_kypseli', label: 'Déballage' },
  { value: 'received', label: 'À contrôler' },
  { value: 'offer', label: 'Contrôlés', requiresDevice: true },
  { value: 'counter_proposal', label: 'Contre offre', requiresDevice: true },
  { value: 'validated', label: 'Validées', group: 'Deal', requiresDevice: true }
];
